import { feature } from 'topojson-client';
import { computeNeighbourhoodScoring } from '../map/neighbourhoodScoring.js';
import { areaStats, fillAreaStats } from '../map/areaData.js';
import { polygonLabelPoint } from '../map/polygonLabelPoint.js';
import { loadAreaData, loadOverlay } from '../map/edgePayloads.js';

// The area page's data (AreaPage.js): the RegSO shapes (/getoverlay) and statistics (/getdata), scored the same
// way as on the map (neighbourhoodScoring.js). Loaded once per page load and shared by every area page visited.

let sourcesPromise = null;

// { topology, colorCache, stats, neighbourhoods } where stats holds the raw values and the `combined_` scores, like
// the views', and neighbourhoods each area's comparison group (code -> the codes it's scored against).
export const loadAreaSources = () => {
    if (!sourcesPromise) {
        sourcesPromise = Promise.all([loadOverlay(), loadAreaData()])
            .then(([topology, data]) => {
                fillAreaStats(data);
                const scoring = computeNeighbourhoodScoring(data, topology) || { colorCache: {}, stats: {}, neighbourhoods: new Map() };
                return {
                    topology,
                    colorCache: scoring.colorCache,
                    stats: { ...areaStats, ...scoring.stats },
                    neighbourhoods: scoring.neighbourhoods,
                };
            });
        sourcesPromise.catch(() => { sourcesPromise = null; });
    }
    return sourcesPromise;
};

// Every area as { code, name, kommun, geometry, bbox }, decoded once per topology.
const featuresByTopology = new WeakMap();

const polygonsOf = (geometry) => {
    if (!geometry) return [];
    if (geometry.type === 'Polygon') return [geometry.coordinates];
    if (geometry.type === 'MultiPolygon') return geometry.coordinates;
    return [];
};

const bboxOf = (geometry) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    polygonsOf(geometry).forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    })));
    return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
};

export const areaFeatures = (topology) => {
    if (!topology?.objects) return [];
    let features = featuresByTopology.get(topology);
    if (!features) {
        const key = Object.keys(topology.objects)[0];
        features = feature(topology, topology.objects[key]).features.map((item) => ({
            code: String(item.properties?.regsokod || '').trim(),
            name: String(item.properties?.regso || '').trim(),
            kommun: String(item.properties?.kommunnamn || '').trim(),
            geometry: item.geometry,
            bbox: bboxOf(item.geometry),
        }));
        featuresByTopology.set(topology, features);
    }
    return features;
};

export const findArea = (topology, code) => areaFeatures(topology).find((item) => item.code === code) || null;

// The outline map: the area and the areas around it as SVG paths, in a view box that holds the area with a
// margin. Longitude is shrunk by cos(latitude), so shapes keep their proportions. The areas around reach well
// past the view box, so a wide or tall map panel shows neighbours to its edges too.
export const areaMapShapes = (topology, code) => {
    const features = areaFeatures(topology);
    const main = features.find((item) => item.code === code);
    if (!main?.bbox) return null;
    const [minX, minY, maxX, maxY] = main.bbox;
    const k = Math.cos((((minY + maxY) / 2) * Math.PI) / 180);
    const project = ([lng, lat]) => [lng * k * 1000, -lat * 1000];
    const width = (maxX - minX) * k * 1000;
    const height = (maxY - minY) * 1000;
    const margin = Math.max(width, height) * 0.4 + 0.5;
    const viewW = width + margin * 2;
    const viewH = height + margin * 2;
    const cx = ((minX + maxX) / 2) * k * 1000;
    const cy = -((minY + maxY) / 2) * 1000;
    const step = Math.max(viewW, viewH) / 600; // skip points closer than ~1px at 600px

    const pathOf = (geometry) => polygonsOf(geometry).map((polygon) => polygon.map((ring) => {
        let last = null;
        const points = [];
        ring.forEach((point, index) => {
            const [x, y] = project(point);
            if (last && index < ring.length - 1 && Math.abs(x - last[0]) < step && Math.abs(y - last[1]) < step) return;
            points.push(`${x.toFixed(2)} ${y.toFixed(2)}`);
            last = [x, y];
        });
        return `M${points.join('L')}Z`;
    }).join('')).join('');

    // Areas within 1.6 view boxes of the centre (in degrees).
    const reachLng = (viewW * 1.6) / (k * 1000);
    const reachLat = (viewH * 1.6) / 1000;
    const centreLng = (minX + maxX) / 2;
    const centreLat = (minY + maxY) / 2;
    const others = features
        .filter((item) => item !== main && item.bbox
            && item.bbox[0] < centreLng + reachLng && item.bbox[2] > centreLng - reachLng
            && item.bbox[1] < centreLat + reachLat && item.bbox[3] > centreLat - reachLat)
        // Big areas first, so an area inside another is drawn over it.
        .sort((a, b) => ((b.bbox[2] - b.bbox[0]) * (b.bbox[3] - b.bbox[1])) - ((a.bbox[2] - a.bbox[0]) * (a.bbox[3] - a.bbox[1])))
        .map((item) => ({ code: item.code, name: item.name, kommun: item.kommun, d: pathOf(item.geometry) }));

    return {
        viewBox: `${(cx - viewW / 2).toFixed(2)} ${(cy - viewH / 2).toFixed(2)} ${viewW.toFixed(2)} ${viewH.toFixed(2)}`,
        box: { x: cx - viewW / 2, y: cy - viewH / 2, w: viewW, h: viewH },
        main: pathOf(main.geometry),
        others,
        project, // [lng, lat] -> the view box's coordinates (a shared place's pin)
    };
};

// The outline map for several areas (a shared comparison): each area's path and middle, and the areas around
// them, in a view box that holds them all with a margin. Same projection as areaMapShapes.
export const areasMapShapes = (topology, codes) => {
    const features = areaFeatures(topology);
    const mains = codes.map((code) => features.find((item) => item.code === code)).filter((item) => item?.bbox);
    if (!mains.length) return null;
    const minX = Math.min(...mains.map((item) => item.bbox[0]));
    const minY = Math.min(...mains.map((item) => item.bbox[1]));
    const maxX = Math.max(...mains.map((item) => item.bbox[2]));
    const maxY = Math.max(...mains.map((item) => item.bbox[3]));
    const k = Math.cos((((minY + maxY) / 2) * Math.PI) / 180);
    const project = ([lng, lat]) => [lng * k * 1000, -lat * 1000];
    const width = (maxX - minX) * k * 1000;
    const height = (maxY - minY) * 1000;
    const margin = Math.max(width, height) * 0.25 + 0.5;
    const viewW = width + margin * 2;
    const viewH = height + margin * 2;
    const cx = ((minX + maxX) / 2) * k * 1000;
    const cy = -((minY + maxY) / 2) * 1000;
    const step = Math.max(viewW, viewH) / 600;
    const pathOf = (geometry) => polygonsOf(geometry).map((polygon) => polygon.map((ring) => {
        let last = null;
        const points = [];
        ring.forEach((point, index) => {
            const [x, y] = project(point);
            if (last && index < ring.length - 1 && Math.abs(x - last[0]) < step && Math.abs(y - last[1]) < step) return;
            points.push(`${x.toFixed(2)} ${y.toFixed(2)}`);
            last = [x, y];
        });
        return `M${points.join('L')}Z`;
    }).join('')).join('');
    // An area's middle for its number: the point deepest inside its largest part (polygonLabelPoint), projected.
    const middleOf = (geometry) => {
        let best = null;
        let bestArea = -1;
        polygonsOf(geometry).forEach((polygon) => {
            const rings = polygon.map((ring) => ring.map(project));
            const outer = rings[0] || [];
            let area = 0;
            for (let i = 0, j = outer.length - 1; i < outer.length; j = i, i += 1) {
                area += (outer[j][0] * outer[i][1]) - (outer[i][0] * outer[j][1]);
            }
            if (Math.abs(area) > bestArea) {
                bestArea = Math.abs(area);
                best = rings;
            }
        });
        return best ? polygonLabelPoint(best) : [cx, cy];
    };
    const codeSet = new Set(mains.map((item) => item.code));
    const reachLng = (viewW * 1.6) / (k * 1000);
    const reachLat = (viewH * 1.6) / 1000;
    const centreLng = (minX + maxX) / 2;
    const centreLat = (minY + maxY) / 2;
    const others = features
        .filter((item) => !codeSet.has(item.code) && item.bbox
            && item.bbox[0] < centreLng + reachLng && item.bbox[2] > centreLng - reachLng
            && item.bbox[1] < centreLat + reachLat && item.bbox[3] > centreLat - reachLat)
        .sort((a, b) => ((b.bbox[2] - b.bbox[0]) * (b.bbox[3] - b.bbox[1])) - ((a.bbox[2] - a.bbox[0]) * (a.bbox[3] - a.bbox[1])))
        .map((item) => ({ code: item.code, name: item.name, kommun: item.kommun, d: pathOf(item.geometry) }));
    return {
        viewBox: `${(cx - viewW / 2).toFixed(2)} ${(cy - viewH / 2).toFixed(2)} ${viewW.toFixed(2)} ${viewH.toFixed(2)}`,
        box: { x: cx - viewW / 2, y: cy - viewH / 2, w: viewW, h: viewH },
        areas: mains.map((item) => ({ code: item.code, d: pathOf(item.geometry), middle: middleOf(item.geometry) })),
        others,
    };
};
